function toJsDate(val) {
  if (!val) return null;
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  const parsed = new Date(val);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatQuoteYmd(date) {
  const d = toJsDate(date) || new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

export function formatQuoteNumber(quoteNumber, puntoVenta = 8) {
  const pv = String(Number(puntoVenta) || 8).padStart(5, '0');
  const n = String(Number(quoteNumber) || 0).padStart(8, '0');
  return `PRE-${pv}-${n}`;
}

export function formatQuoteValidUntilDisplay(val) {
  const d = toJsDate(val);
  if (!d) return '';
  return d.toLocaleDateString('es-AR');
}

export const DEFAULT_QUOTE_CURRENCY = 'Pesos Argentinos';
export const DEFAULT_QUOTE_DESTINATION = 'Córdoba';
export const DEFAULT_QUOTE_PAYMENT = 'A convenir';
export const DEFAULT_QUOTE_VALID_DAYS = 15;

export function addDaysToDate(date, days) {
  const d = toJsDate(date) || new Date();
  const next = new Date(d.getTime());
  next.setDate(next.getDate() + (Number(days) || 0));
  return next;
}

export function toDateInputValue(val) {
  const d = toJsDate(val);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseLocalDateInput(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function getQuoteTerms(quote) {
  const currency = String(quote?.currency || '').trim() || DEFAULT_QUOTE_CURRENCY;
  const destinationProvince = String(quote?.destinationProvince || '').trim() || DEFAULT_QUOTE_DESTINATION;
  const paymentTerms = String(quote?.paymentTerms || '').trim() || DEFAULT_QUOTE_PAYMENT;
  return {
    currency,
    destinationProvince,
    paymentTerms,
    validUntil: quote?.validUntil || null,
    includeImages: quote?.includeImages === true,
    includeDescription: quote?.includeDescription === true
  };
}

export function getQuoteProductImageUrl(product) {
  if (!product || typeof product !== 'object') return '';
  const arr = product.images || product.Images;
  if (Array.isArray(arr) && arr.length > 0) {
    const first = arr[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (first && typeof first === 'object') {
      const url = first.url || first.src || first.downloadURL;
      if (url && String(url).trim()) return String(url).trim();
    }
  }
  if (typeof product.image === 'string' && product.image.trim()) return product.image.trim();
  return '';
}

export function getQuoteProductDescription(product, maxLen = 280) {
  const raw = product?.description || product?.descripcion || '';
  const text = String(raw)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function getQuoteLinePrices(product, saleData) {
  const qty = Number(product?.quantity) || 0;
  const listPrice = Number(product?.price) || 0;
  const lineList = roundMoney(listPrice * qty);
  const products = saleData?.products || [];
  const subtotal = Number(saleData?.subtotal) || products.reduce(
    (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
    0
  );
  const discountAmount = Number(saleData?.discountAmount) || 0;
  const discountType = saleData?.discountType || null;
  const discountValue = saleData?.discount != null ? Number(saleData.discount) : null;
  const hasDiscount = discountAmount > 0 && subtotal > 0;
  if (!hasDiscount) {
    return {
      listPrice: roundMoney(listPrice),
      discountedUnit: roundMoney(listPrice),
      lineList,
      lineDiscounted: lineList,
      hasDiscount: false
    };
  }
  let factor = Math.max(0, 1 - discountAmount / subtotal);
  if (discountType === 'percentage' && discountValue != null) {
    factor = Math.max(0, 1 - discountValue / 100);
  }
  return {
    listPrice: roundMoney(listPrice),
    discountedUnit: roundMoney(listPrice * factor),
    lineList,
    lineDiscounted: roundMoney(lineList * factor),
    hasDiscount: true
  };
}

export function buildQuoteInvoiceData(quote) {
  const pv = Number(quote?.puntoVenta ?? quote?.invoiceOptions?.puntoVenta) || 8;
  const n = Number(quote?.quoteNumber ?? quote?.orderNumber) || 0;
  const terms = getQuoteTerms(quote);
  return {
    isQuote: true,
    tipo_comprobante: 'PRE',
    punto_venta: pv,
    numero_comprobante: n,
    numero: formatQuoteNumber(n, pv),
    fecha_emision: formatQuoteYmd(quote?.date),
    total: Number(quote?.totalPrice) || 0,
    validUntil: terms.validUntil,
    currency: terms.currency,
    destinationProvince: terms.destinationProvince,
    paymentTerms: terms.paymentTerms,
    includeImages: terms.includeImages,
    includeDescription: terms.includeDescription
  };
}
